// ============================================================================
// CANCUN100 — TopToursPreciosTab.js  (Lista de Precios TOP TOURS · Bloque 2)
// ----------------------------------------------------------------------------
// LECTURA: vista top_tours_lista (un renglón por producto activo de la biblia,
// con el precio TT ya resuelto: editado por Maggie o sugerido).
// ESCRITURA SOLO en top_tours_precios, SIEMPRE upsert por sku. NUNCA .delete():
// "regresar al sugerido" = precio_publico_* = null; apagar = activo=false.
// Nunca toca la biblia (productos), netos, comisiones ni la Lista de Sala.
//
// Exporta:
//   default TopToursPreciosTab  → modo 'admin' (editable si `editable`) o
//                                  modo 'agente' (solo activo_tt, solo lectura).
//   ComparativoPreciosTab        → Sala (biblia) vs Top Tours, solo lectura.
// Colores: objeto C (tema-aware) + tokens Aurelia. No convierte C a CSS.
// ============================================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabaseClient';
import DescriptivoBoton from './descriptivos/DescriptivoBoton';
import { RADIO, RADIO_TARJETA, RADIO_INSIGNIA } from './tema';
import Segmentado from './Segmentado';
import Stepper from './Stepper'; // APP 2/05 · F1
import { marcarNoDisponibles, noDisp, ChipNoDisponible } from './SalaPreciosTab'; // B55 (APP 2/05 F3)
import useBorrador, { claveBorrador, BandaBorrador } from './useBorrador'; // Bloque 27
import { COLS_AGENTE, COLS_ADMIN, ALTO_FILA, TablaPareja, CeldaNota, GloboNota, FilaMovil, DatoMovil, useMovil, tdP, numP, clamp2, linea1 } from './ListaPareja'; // APP 2/28 (RP-10)
import { Ic } from './iconos'; // APP 2/34: íconos Lucide en vez de emoji
const INICIAL_TMP = { proveedor: '', otro: '', nombre: '', moneda: '', nacional: null, tipo: 'pax', cubreAd: '2', cubreMe: '0', netoAd: '', netoMe: '', notas: '' };
const esVacioTmp = (e) => !e || (!String(e.nombre || '').trim() && !String(e.netoAd || '').trim() && !String(e.notas || '').trim());

// La paleta global no trae rojo: estos componentes usan su propio rojo vivo (se nota en claro y oscuro).

const F2 = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (n, moneda) => (n == null || n === '' ? '—' : `${moneda === 'USD' ? 'USD $' : '$'}${F2.format(Number(n))}`);
const numOrNull = (v) => (v === '' || v == null || isNaN(Number(v)) ? null : Number(v));
const mismo = (a, b) => (a == null && b == null) || (a != null && b != null && Math.abs(Number(a) - Number(b)) < 0.005);

// Sección del renglón: la vista ya la trae (1 ⭐ · 2 normal · 3 promociones).
const seccionDe = (r) => Number(r.seccion) || (r.estrella ? 1 : r.es_promocion ? 3 : 2);

// Grupo base del renglón SIN contar la ⭐: 3 = promociones, 2 = normal. Un producto con ⭐ sale en
// "⭐ Top" y TAMBIÉN en su proveedor (con la estrella prendida), para que no desaparezca de donde se marcó.
const grupoBase = (r) => (r.es_promocion || Number(r.seccion) === 3 ? 3 : 2);

// Clave/etiqueta de proveedor del renglón (Bloque 5: un temporal con proveedor "Otro" se agrupa bajo su texto).
const provKey = (r) => r.proveedor_nombre || r.proveedor_otro || r.proveedor_codigo || 'Sin proveedor';
// B55 (APP 2/05 F3): «No disponible» (activo y reservable = false). Se ve en gris, con chip, al final de su proveedor.
const ordenNoDisp = (a, b) => (noDisp(a) ? 1 : 0) - (noDisp(b) ? 1 : 0);

// Agrupa en el orden pedido: ⭐ Top (orden_top) → proveedores A–Z (nombre) → Promociones por proveedor.
export const agrupar = (rows) => {
  // Bloque 12 §2: "⭐ Top" = productos con estrella (por orden_top), primero; luego proveedores A–Z y promociones.
  const top = rows.filter((r) => r.estrella === true || (r.estrella == null && seccionDe(r) === 1))
    .sort((a, b) => ordenNoDisp(a, b) || (a.orden_top ?? 1e9) - (b.orden_top ?? 1e9) || String(a.nombre).localeCompare(String(b.nombre), 'es'));
  const porProv = (lista) => {
    const m = {};
    lista.forEach((r) => {
      const k = provKey(r);
      (m[k] = m[k] || []).push(r);
    });
    return Object.keys(m).sort((a, b) => a.localeCompare(b, 'es')).map((k) => ({
      proveedor: k,
      codigo: m[k][0].proveedor_codigo,
      items: m[k].sort((a, b) => ordenNoDisp(a, b) || String(a.nombre).localeCompare(String(b.nombre), 'es')), // B55: «No disponible» al final de su proveedor
    }));
  };
  return {
    top,
    normales: porProv(rows.filter((r) => grupoBase(r) === 2)),
    promos: porProv(rows.filter((r) => grupoBase(r) === 3)),
  };
};

// Tarea 3b — alerta "precio arriba de Sala": no promoción y precio TT > público biblia.
const arriba = (tt, sala) => tt != null && sala != null && Number(tt) > Number(sala) + 0.005;
const enAlerta = (r) => !r.es_promocion && (arriba(r.precio_adulto, r.precio_biblia_adulto) || arriba(r.precio_menor, r.precio_biblia_menor));
const SOBRE_STD = { MXN: 299, USD: 19.99 };

const coincide = (r, q) => !q || [r.nombre, r.sku, r.proveedor_nombre, r.proveedor_codigo, r.notas]
  .some((x) => String(x || '').toLowerCase().includes(q));

// ── Piezas visuales compartidas ──
const th = (C) => ({ padding: '8px 10px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: C.muted, letterSpacing: '.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' });
const td = { padding: '8px 10px', verticalAlign: 'middle' };
const numTd = (C, strong) => ({ ...td, fontFamily: 'var(--au-font-text)', whiteSpace: 'nowrap', color: strong ? C.text : C.muted, fontWeight: strong ? 700 : 400 });

const MonedaBadge = ({ C, moneda }) => (
  <span style={{ background: moneda === 'USD' ? `${C.blue}18` : `${C.naranja}18`, color: moneda === 'USD' ? C.blue : C.naranja, padding: '2px 6px', borderRadius: RADIO_INSIGNIA, fontSize:12, fontWeight: 700 }}>{moneda || 'MXN'}</span>
);

const Grupo = ({ C, titulo, derecha, children, acento }) => (
  <div className="au-glass" style={{ marginBottom: 12, padding: 0, overflow: 'hidden' }}>
    <div style={{ padding: '12px 16px', background: `${acento || C.rosa}10`, borderBottom: `1px solid ${acento || C.rosa}22`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <div style={{ fontWeight: 700, color: C.text, fontSize: 14 }}>{titulo}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>{derecha}</div>
    </div>
    <div style={{ overflowX: 'auto' }}>{children}</div>
  </div>
);

const Buscador = ({ C, value, onChange, derecha, children }) => (
  <div className="au-glass" style={{ marginBottom: 12, padding: '12px 16px' }}>
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
          placeholder="Buscar por nombre, SKU, proveedor o notas..."
          style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: RADIO, padding: '10px 12px 10px 34px', color: C.text, fontSize: 14, fontFamily: 'var(--au-font-text)', outline: 'none', boxSizing: 'border-box' }} />
        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, opacity: 0.4 }}>{<Ic n="Search" chico />}</span>
      </div>
      {children}
      <div style={{ fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>{derecha}</div>
    </div>
  </div>
);

const Titulo = ({ children, right }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, marginTop: 28, flexWrap: 'wrap', gap: 8 }}>
    <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--au-text-primary)', fontFamily: 'var(--au-font-text)', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ width: 3, height: 16, background: 'var(--au-raya)', borderRadius: 2, display: 'inline-block' }} />
      {children}
    </h2>
    {right}
  </div>
);

const useLista = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    const { data, error: e } = await supabase.from('top_tours_lista').select('*');
    if (e) setError(e.message || 'No se pudo cargar la lista de Top Tours');
    else { setError(''); setRows(await marcarNoDisponibles(data || [])); } // B55
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  return { rows, setRows, loading, error, load };
};

// ════════════════════════════════════════════════════════════════════════════
// Bloque 5 — PRODUCTOS TEMPORALES (top_tours_temporales: insert/update, nunca delete)
// ════════════════════════════════════════════════════════════════════════════
const sugeridoDe = (neto, moneda) => (neto == null || neto === '' || isNaN(Number(neto)) ? null : Number(neto) + (moneda === 'USD' ? 19.99 : 299));

// Ventana modal a nivel de módulo (si viviera dentro del render, los inputs perderían el foco).
const Ventana = ({ C, titulo, onClose, children, ancho = 520 }) => (
  <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
    <div onClick={(e) => e.stopPropagation()} className="au-glass" style={{ background: C.card || C.surface, width: '100%', maxWidth: ancho, maxHeight: '90vh', overflowY: 'auto', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{titulo}</div>
        <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer' }}>{<Ic n="X" chico />}</button>
      </div>
      {children}
    </div>
  </div>
);

// Alta / edición de un producto temporal (Maggie y Marco).
function TemporalForm({ C: Cbase, modo, sku, temporalId, quien, onClose, onGuardado }) {
  const C = Cbase; // APP 2/20 Fase A: C.red viene del tema
  const [provs, setProvs] = useState([]);
  const [f, setF] = useState(INICIAL_TMP);
  const borradorTmp = useBorrador(claveBorrador('temporal-tt', quien), f, setF, { activo: modo !== 'editar', inicial: INICIAL_TMP, esVacio: esVacioTmp }); // Bloque 27
  const [errs, setErrs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cargando, setCargando] = useState(modo === 'editar');
  const [skuNuevo, setSkuNuevo] = useState(null);
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('proveedores').select('codigo,nombre').eq('activo', true).order('nombre');
      setProvs(data || []);
      if (modo === 'editar' && sku) {
        const { data: t } = await supabase.from('top_tours_temporales').select('*').eq('id', temporalId).maybeSingle();
        if (t) setF({
          proveedor: t.proveedor_codigo || (t.proveedor_otro ? 'OTRO' : ''), otro: t.proveedor_otro || '', nombre: t.nombre || '',
          moneda: t.moneda || '', nacional: t.es_nacional, tipo: t.unidad_tipo || 'pax',
          cubreAd: String(t.unidad_cubre_adultos ?? 2), cubreMe: String(t.unidad_cubre_menores ?? 0),
          netoAd: t.precio_neto_adulto ?? '', netoMe: t.precio_neto_menor ?? '', notas: t.notas || '',
        });
        setCargando(false);
      }
    })();
  }, [modo, sku, temporalId]);
  const set = (patch) => { setF((x) => ({ ...x, ...patch })); setErrs([]); };
  const esPromo = f.tipo === 'promo';
  const validar = () => {
    const e = [];
    if (!f.proveedor) e.push('Elige el proveedor.');
    if (f.proveedor === 'OTRO' && !f.otro.trim()) e.push('Escribe el nombre del proveedor ("Otro").');
    if (!f.nombre.trim()) e.push('Escribe el nombre del tour.');
    if (f.moneda !== 'MXN' && f.moneda !== 'USD') e.push('Elige la moneda.');
    if (f.nacional !== true && f.nacional !== false) e.push('Indica si es nacional.');
    if (esPromo) {
      const ad = Number(f.cubreAd), me = Number(f.cubreMe);
      if (!Number.isInteger(ad) || ad < 1) e.push('La promoción debe cubrir al menos 1 adulto.');
      if (!Number.isInteger(me) || me < 0) e.push('Los menores que cubre deben ser 0 o más.');
    }
    if (!(Number(f.netoAd) > 0)) e.push('El neto adulto debe ser mayor a 0.');
    if (!esPromo && f.netoMe !== '' && (isNaN(Number(f.netoMe)) || Number(f.netoMe) < 0)) e.push('El neto menor no es válido.');
    return e;
  };
  const guardar = async () => {
    const e = validar();
    if (e.length) { setErrs(e); return; }
    const datos = {
      nombre: f.nombre.trim(),
      proveedor_codigo: f.proveedor === 'OTRO' ? null : f.proveedor,
      proveedor_otro: f.proveedor === 'OTRO' ? f.otro.trim() : null,
      moneda: f.moneda, es_nacional: f.nacional, unidad_tipo: esPromo ? 'promo' : 'pax',
      unidad_cubre_adultos: esPromo ? Number(f.cubreAd) : 1, unidad_cubre_menores: esPromo ? Number(f.cubreMe) : 0,
      precio_neto_adulto: Number(f.netoAd),
      precio_neto_menor: esPromo || f.netoMe === '' ? null : Number(f.netoMe),
      notas: f.notas.trim() || null,
      actualizado_por: quien,
    };
    setBusy(true);
    if (modo === 'editar') {
      const { error } = await supabase.from('top_tours_temporales').update(datos).eq('id', temporalId);
      setBusy(false);
      if (error) { setErrs([`No se pudo guardar: ${error.message}`]); return; }
      await onGuardado(`Temporal ${sku} actualizado`);
      onClose();
    } else {
      const { data, error } = await supabase.from('top_tours_temporales').insert({ ...datos, creado_por: quien }).select('sku').single();
      setBusy(false);
      if (error) { setErrs([`No se pudo guardar: ${error.message}`]); return; }
      borradorTmp.limpiar(); // Bloque 27
      setSkuNuevo(data?.sku || '(sin SKU)');
      await onGuardado(`Temporal agregado: ${data?.sku || ''}`);
    }
  };
  const input = { width: '100%', background: C.dark, border: `1px solid ${C.border}`, borderRadius: RADIO, height: 40, boxSizing: 'border-box', padding: '0 12px', color: C.text, fontSize: 14, fontFamily: 'var(--au-font-text)', outline: 'none', boxSizing: 'border-box' };
  const lbl = { fontSize: 14, color: C.muted, fontWeight: 600, marginBottom: 6, display: 'block' };
  const sugAd = sugeridoDe(f.netoAd, f.moneda), sugMe = esPromo ? null : sugeridoDe(f.netoMe, f.moneda);
  const mon = f.moneda || 'MXN';

  if (skuNuevo) {
    return (
      <Ventana C={C} titulo="✅ Producto temporal agregado" onClose={onClose}>
        <div style={{ fontSize: 14, color: C.text, lineHeight: 1.7 }}>
          <div><strong>SKU asignado:</strong> <span style={{ fontFamily: 'var(--au-font-text)', color: C.yellow }}>{skuNuevo}</span></div>
          <div><strong>Tour:</strong> {f.nombre}</div>
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>Ya aparece en la lista (en ámbar) y en "+ Reserva" de los agentes Top Tours.</div>
        <div className="au-pie">
          <button type="button" className="au-btn au-btn--primary" onClick={onClose}>Listo</button>
        </div>
      </Ventana>
    );
  }
  return (
    <Ventana C={C} titulo={modo === 'editar' ? `Editar temporal · ${sku}` : '➕ Agregar producto temporal'} onClose={onClose}>
      {cargando ? <div style={{ color: C.muted, padding: 20 }}>Cargando…</div> : (<>
        <BandaBorrador C={C} borrador={borradorTmp} compacta />
        {errs.length > 0 && <div className="au-banner au-banner--error" style={{ marginBottom: 12, fontSize: 12 }}>{errs.map((x) => <div key={x}>{x}</div>)}</div>}
        <label style={lbl}>Proveedor</label>
        <select style={{ ...input, marginBottom: f.proveedor === 'OTRO' ? 6 : 10 }} value={f.proveedor} onChange={(e) => set({ proveedor: e.target.value })}>
          <option value="">— Elige proveedor —</option>
          {provs.map((p) => <option key={p.codigo} value={p.codigo}>{p.nombre} ({p.codigo})</option>)}
          <option value="OTRO">Otro…</option>
        </select>
        {f.proveedor === 'OTRO' && <input style={{ ...input, marginBottom: 14 }} placeholder="Nombre del proveedor" value={f.otro} onChange={(e) => set({ otro: e.target.value })} />}
        <label style={lbl}>Nombre del tour</label>
        <input style={{ ...input, marginBottom: 14 }} value={f.nombre} onChange={(e) => set({ nombre: e.target.value })} />
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <div><label style={lbl}>Moneda</label><Segmentado valor={f.moneda} opciones={[['MXN', 'MXN'], ['USD', 'USD']]} onChange={(m) => set({ moneda: m })} /></div>
          <div><label style={lbl}>Nacional</label><Segmentado valor={f.nacional} opciones={[[true, 'Sí'], [false, 'No']]} onChange={(v) => set({ nacional: v })} /></div>
          <div><label style={lbl}>Tipo</label><Segmentado valor={f.tipo} opciones={[['pax', 'Por persona'], ['promo', 'Promoción']]} onChange={(v) => set({ tipo: v })} /></div>
        </div>
        {esPromo && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <div style={{ flex: 1 }}><label style={lbl}>¿Cuántos adultos cubre?</label><Stepper chico value={f.cubreAd} min={1} max={20} label="adultos que cubre" onChange={(n) => set({ cubreAd: String(n) })} /></div>
            <div style={{ flex: 1 }}><label style={lbl}>¿Cuántos menores?</label><Stepper chico value={f.cubreMe} min={0} max={20} label="menores que cubre" onChange={(n) => set({ cubreMe: String(n) })} /></div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}><label style={lbl}>{esPromo ? 'Neto por promoción' : 'Neto adulto'}</label><input type="number" min="0" step="0.01" style={input} value={f.netoAd} onChange={(e) => set({ netoAd: e.target.value })} /></div>
          <div style={{ flex: 1 }}><label style={lbl}>Neto menor (vacío = no se vende a menores)</label><input type="number" min="0" step="0.01" disabled={esPromo} style={{ ...input, opacity: esPromo ? 0.4 : 1 }} value={esPromo ? '' : f.netoMe} onChange={(e) => set({ netoMe: e.target.value })} /></div>
        </div>
        <label style={lbl}>Notas</label>
        <textarea rows={2} style={{ ...input, height: 'auto', minHeight: 72, padding: '10px 12px', resize: 'vertical', marginBottom: 14 }} value={f.notas} onChange={(e) => set({ notas: e.target.value })} />
        <div style={{ background: `${C.yellow}14`, border: `1px solid ${C.yellow}44`, borderRadius: RADIO_TARJETA, padding: '10px 12px', fontSize: 12, color: C.text }}>
          Precio Top Tours sugerido: <strong>{sugAd == null ? '—' : money(sugAd, mon)}</strong>{esPromo ? ' por promoción' : ' adulto'}
          {!esPromo && <> · menor: <strong>{sugMe == null ? 'no se vende' : money(sugMe, mon)}</strong></>}
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Neto + {mon === 'USD' ? 'USD 19.99' : '$299 MXN'}</div>
        </div>
        <div className="au-pie">
          <button type="button" className="au-btn au-btn--ghost" onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="button" className="au-btn au-btn--primary" onClick={guardar} disabled={busy}>{busy ? 'Guardando…' : (modo === 'editar' ? 'Guardar cambios' : 'Agregar temporal')}</button>
        </div>
      </>)}
    </Ventana>
  );
}

// "📋 Pendientes de biblia" (solo Marco): temporales sin ligar + ligar a un SKU de la biblia + historial.
function PendientesBiblia({ C: Cbase, quien, onLigado }) {
  const C = Cbase; // APP 2/20 Fase A: C.red viene del tema
  const [temps, setTemps] = useState([]);
  const [vendidos, setVendidos] = useState({});
  const [provs, setProvs] = useState({});
  const [loading, setLoading] = useState(true);
  const [ligar, setLigar] = useState(null); // { temp, productos, q, elegido, err, busy }
  const cargar = useCallback(async () => {
    const [t, p] = await Promise.all([
      supabase.from('top_tours_temporales').select('*').order('creado_en', { ascending: false }),
      supabase.from('proveedores').select('codigo,nombre'),
    ]);
    const lista = t.data || [];
    setTemps(lista);
    setProvs(Object.fromEntries((p.data || []).map((x) => [x.codigo, x.nombre])));
    // Nº de veces vendido: folios vigentes (no eliminados) cuyo servicios_detalle contiene el SKU TMP.
    const pend = lista.filter((x) => !x.sku_biblia);
    const conteos = await Promise.all(pend.map((x) => supabase.from('reservas').select('id', { count: 'exact', head: true })
      .contains('servicios_detalle', JSON.stringify([{ sku: x.sku }])).eq('es_vigente', true).neq('estado', 'eliminado')
      .then((r) => [x.sku, r.error ? null : r.count])));
    setVendidos(Object.fromEntries(conteos));
    setLoading(false);
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const abrirLigar = async (temp) => {
    setLigar({ temp, productos: [], q: '', elegido: null, err: '', busy: false, cargando: true });
    const { data } = await supabase.from('productos').select('sku,nombre,proveedor_codigo,moneda').eq('activo', true).order('nombre');
    setLigar((l) => l && ({ ...l, productos: data || [], cargando: false }));
  };
  const confirmarLigar = async () => {
    if (!ligar?.elegido) return;
    setLigar((l) => ({ ...l, busy: true, err: '' }));
    const { data, error } = await supabase.rpc('tt_ligar_temporal', { p_temporal_id: ligar.temp.id, p_sku_biblia: ligar.elegido.sku, p_por: quien });
    if (error || !data?.ok) { setLigar((l) => ({ ...l, busy: false, err: error?.message || data?.error || 'No se pudo ligar' })); return; }
    const msg = `${ligar.temp.sku} ligado a ${ligar.elegido.sku}`;
    setLigar(null);
    await cargar();
    await onLigado(msg);
  };

  const pendientes = temps.filter((x) => !x.sku_biblia);
  const ligados = temps.filter((x) => x.sku_biblia);
  const provDe = (x) => (x.proveedor_codigo ? (provs[x.proveedor_codigo] || x.proveedor_codigo) : (x.proveedor_otro ? `${x.proveedor_otro} (otro)` : '—'));
  const fecha = (v) => (v ? new Date(v).toLocaleString('es-MX', { timeZone: 'America/Cancun', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
  const thS = th(C);
  const tdS = { ...td, fontSize: 12, color: C.text };
  const ql = (ligar?.q || '').trim().toLowerCase();
  const opciones = ligar ? ligar.productos.filter((p) => !ql || p.sku.toLowerCase().includes(ql) || String(p.nombre).toLowerCase().includes(ql)).slice(0, 25) : [];

  if (loading) return <div style={{ padding: 30, textAlign: 'center', color: C.muted }}>Cargando pendientes de biblia…</div>;
  return (
    <>
      <Titulo>{<Ic n="ClipboardList" chico />} Pendientes de biblia ({pendientes.length})</Titulo>
      <div className="au-glass" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {['SKU', 'Nombre', 'Proveedor', 'Mon', 'Nal', 'Tipo / cubre', 'Neto Ad', 'Neto Men', 'Notas', 'Creado por', 'Fecha', 'Vendido', ''].map((h) => <th key={h} style={thS}>{h}</th>)}
            </tr></thead>
            <tbody>
              {pendientes.length === 0 ? (
                <tr><td colSpan={13} style={{ padding: 30, textAlign: 'center', color: C.muted }}>Sin productos temporales pendientes.</td></tr>
              ) : pendientes.map((x) => (
                <tr key={x.id} style={{ borderBottom: `1px solid ${C.border}`, background: `${C.yellow}10`, opacity: x.activo === false ? 0.55 : 1 }}>
                  <td style={{ ...tdS, fontFamily: 'var(--au-font-text)', fontWeight: 700, color: C.yellow, whiteSpace: 'nowrap' }}>{x.sku}</td>
                  <td style={tdS}>{x.nombre}{x.activo === false && <span className="au-badge au-badge--neutral" style={{ fontSize: 12, marginLeft: 6 }}>apagado</span>}</td>
                  <td style={{ ...tdS, color: C.muted }}>{provDe(x)}</td>
                  <td style={tdS}><MonedaBadge C={C} moneda={x.moneda} /></td>
                  <td style={tdS}>{x.es_nacional ? 'Sí' : 'No'}</td>
                  <td style={{ ...tdS, whiteSpace: 'nowrap' }}>{x.unidad_tipo === 'promo' ? `Promo ${x.unidad_cubre_adultos}A/${x.unidad_cubre_menores}M` : 'Por persona'}</td>
                  <td style={numTd(C)}>{money(x.precio_neto_adulto, x.moneda)}</td>
                  <td style={numTd(C)}>{money(x.precio_neto_menor, x.moneda)}</td>
                  <td style={{ ...tdS, fontSize: 12, color: C.muted, maxWidth: 180 }}>{x.notas || '—'}</td>
                  <td style={{ ...tdS, color: C.muted }}>{x.creado_por || '—'}</td>
                  <td style={{ ...tdS, color: C.muted, whiteSpace: 'nowrap' }}>{fecha(x.creado_en)}</td>
                  <td style={tdS}>{vendidos[x.sku] == null ? '—' : vendidos[x.sku]}</td>
                  <td style={tdS}><button type="button" className="au-btn au-btn--secondary" onClick={() => abrirLigar(x)} style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12, whiteSpace: 'nowrap' }}>Ligar a la biblia</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Titulo>Historial de temporales ligados</Titulo>
      <div className="au-glass" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>{['Temporal', 'Nombre', '→ SKU biblia', 'Ligado por', 'Fecha'].map((h) => <th key={h} style={thS}>{h}</th>)}</tr></thead>
            <tbody>
              {ligados.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: C.muted }}>Todavía no se ha ligado ningún temporal.</td></tr>
              ) : ligados.map((x) => (
                <tr key={x.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ ...tdS, fontFamily: 'var(--au-font-text)', color: C.muted }}>{x.sku}</td>
                  <td style={tdS}>{x.nombre}</td>
                  <td style={{ ...tdS, fontFamily: 'var(--au-font-text)', fontWeight: 700, color: C.green }}>{x.sku_biblia}</td>
                  <td style={{ ...tdS, color: C.muted }}>{x.ligado_por || '—'}</td>
                  <td style={{ ...tdS, color: C.muted, whiteSpace: 'nowrap' }}>{fecha(x.ligado_en)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {ligar && (
        <Ventana C={C} titulo={`Ligar ${ligar.temp.sku} a la biblia`} onClose={() => !ligar.busy && setLigar(null)}>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>{ligar.temp.nombre} · {provDe(ligar.temp)}</div>
          {ligar.err && <div className="au-banner au-banner--error" style={{ marginBottom: 14, fontSize: 12 }}>{ligar.err}</div>}
          {ligar.cargando ? <div style={{ color: C.muted }}>Cargando biblia…</div> : (<>
            <input autoFocus value={ligar.q} onChange={(e) => setLigar((l) => ({ ...l, q: e.target.value, elegido: null }))} placeholder="Buscar SKU o nombre en la biblia…"
              style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: RADIO, padding: '9px 12px', color: C.text, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 8 }} />
            <div style={{ maxHeight: 260, overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: RADIO_TARJETA }}>
              {opciones.length === 0 ? <div style={{ padding: 14, color: C.muted, fontSize: 12 }}>Sin coincidencias.</div> : opciones.map((p) => (
                <div key={p.sku} onClick={() => setLigar((l) => ({ ...l, elegido: p }))}
                  style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: `1px solid ${C.border}`, background: ligar.elegido?.sku === p.sku ? `${C.green}18` : 'transparent', fontSize: 12, color: C.text }}>
                  <strong style={{ fontFamily: 'var(--au-font-text)' }}>{p.sku}</strong> · {p.nombre} <span style={{ color: C.muted }}>({p.proveedor_codigo} · {p.moneda})</span>
                </div>
              ))}
            </div>
            {ligar.elegido && (
              <div style={{ marginTop: 10, fontSize: 12, color: C.text, background: `${C.green}12`, border: `1px solid ${C.green}44`, borderRadius: RADIO_TARJETA, padding: '8px 12px' }}>
                Se copiarán {<Ic n="Star" chico />}, orden, precios e "incluye" de {ligar.temp.sku} a <strong>{ligar.elegido.sku}</strong>, y el temporal dejará de salir en la lista.
              </div>
            )}
          </>)}
          <div className="au-pie">
            <button type="button" className="au-btn au-btn--ghost" onClick={() => setLigar(null)} disabled={ligar.busy}>Cancelar</button>
            <button type="button" className="au-btn au-btn--primary" onClick={confirmarLigar} disabled={!ligar.elegido || ligar.busy}>{ligar.busy ? 'Ligando…' : 'Confirmar y ligar'}</button>
          </div>
        </Ventana>
      )}
    </>
  );
}

// Bloque 12 §1c — PDF de la LISTA completa de Top Tours (uso interno del agente/admin, CON netos;
// NO es el PDF del cliente). Solo lo prendido. jsPDF por CDN, igual que la lista de Sala.
const descargarListaTT = async (filas) => {
  try {
    if (!window.jspdf) {
      const cargar = (src) => new Promise((resolve, reject) => { const s = document.createElement('script'); s.src = src; s.onload = resolve; s.onerror = reject; document.head.appendChild(s); });
      await cargar('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
      await cargar('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js');
    }
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pm = (n, mon) => (n != null ? `${mon === 'USD' ? 'USD $' : '$'}${F2.format(Number(n))}` : '-');
    const porProv = {};
    filas.forEach((r) => { (porProv[provKey(r)] = porProv[provKey(r)] || []).push(r); });
    const claves = Object.keys(porProv).sort((a, b) => a.localeCompare(b, 'es'));
    doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(247, 71, 128);
    doc.text('CANCUN100 — LISTA DE PRECIOS TOP TOURS', 14, 15);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(100, 100, 100);
    doc.text(`${new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })} · ${filas.length} productos · Precio TT = neto + $299 MXN / + US$19.99 · USO INTERNO`, 14, 21);
    doc.setDrawColor(247, 71, 128); doc.setLineWidth(0.4); doc.line(14, 24, 196, 24);
    let y = 30;
    claves.forEach((k) => {
      const items = porProv[k].sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), 'es'));
      if (y > 260) { doc.addPage(); y = 15; }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(247, 71, 128);
      doc.text(`${k} — ${items.length} producto${items.length !== 1 ? 's' : ''}`, 14, y);
      doc.autoTable({
        startY: y + 3,
        head: [['SKU', 'PRODUCTO', 'MON', 'NETO AD', 'PRECIO TT AD', 'NETO ME', 'PRECIO TT ME', 'NAL', 'NOTAS']],
        body: items.map((r) => [r.sku || '', `${r.estrella ? '* ' : ''}${r.nombre || ''}${r.es_temporal ? ' (temporal)' : ''}`, r.moneda || 'MXN',
          pm(r.precio_neto_adulto, r.moneda), pm(r.precio_adulto, r.moneda), pm(r.precio_neto_menor, r.moneda), pm(r.precio_menor, r.moneda),
          r.es_nacional ? 'NAL' : 'EXT', r.notas || '']),
        styles: { fontSize: 12, cellPadding: 1.5, overflow: 'linebreak' },
        headStyles: { fillColor: [247, 71, 128], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 12 },
        columnStyles: { 0: { cellWidth: 16, fontStyle: 'bold' }, 1: { cellWidth: 50 }, 2: { cellWidth: 10, halign: 'center' }, 3: { cellWidth: 17, halign: 'right' }, 4: { cellWidth: 18, halign: 'right' }, 5: { cellWidth: 17, halign: 'right' }, 6: { cellWidth: 18, halign: 'right' }, 7: { cellWidth: 10, halign: 'center' }, 8: { cellWidth: 26 } },
        theme: 'grid',
      });
      y = doc.lastAutoTable.finalY + 6;
    });
    const n = doc.internal.getNumberOfPages();
    for (let i = 1; i <= n; i++) {
      doc.setPage(i); doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(140, 140, 140);
      doc.text('USO INTERNO — no compartir con clientes · cancun100.com by Xpectative Travel', 14, 292);
      doc.text(`Pagina ${i} de ${n}`, 180, 292);
    }
    doc.save(`Lista_Precios_TopTours_${new Date().toISOString().slice(0, 10)}.pdf`);
  } catch (e) {
    console.error('Error generando PDF de lista TT:', e && e.message);
    alert('No se pudo generar el PDF. Intenta de nuevo.');
  }
};

// ════════════════════════════════════════════════════════════════════════════
// TOP TOURS — editor (admin) / lista (agente)
// ════════════════════════════════════════════════════════════════════════════
export default function TopToursPreciosTab({ C: Cbase, loggedAgent = null, modo = 'admin', editable = false, esSuperAdmin = false, opcionesPrecioPDF = null, leyendasBloqueadasPDF = false }) {
  const C = Cbase; // APP 2/20 Fase A: C.red viene del tema
  const esAgente = modo === 'agente';
  const puedeEditar = !esAgente && editable;
  const { rows, setRows, loading, error, load } = useLista();
  const [q, setQ] = useState('');
  const [mostrarApagados, setMostrarApagados] = useState(true);
  const [drafts, setDrafts] = useState({}); // sku → { ad, men } (texto de los inputs)
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null); // { tipo:'ok'|'error', msg }
  const [soloAlerta, setSoloAlerta] = useState(false); // 3b: "Ver solo estos"
  const [globo, setGlobo] = useState(null);            // 3b: { sku, top, left } del popover abierto
  const inputsAd = useRef({});                          // 3b: "Editar precio" enfoca el input
  const [globoT, setGloboT] = useState(null);          // Bloque 5: { sku, top, left } del globo "Temporal"
  const [formTmp, setFormTmp] = useState(null);        // Bloque 5: { modo:'alta'|'editar', sku? }
  const [vista, setVista] = useState('lista');         // Bloque 5: 'lista' | 'pendientes' (solo super_admin)
  const [nPendientes, setNPendientes] = useState(null);
  const movil = useMovil(); // APP 2/28: renglón compacto en el celular
  const [globoN, setGloboN] = useState(null); // APP 2/28: { r, pos } del globo ⓘ de la nota de venta
  const [abiertoM, setAbiertoM] = useState(null); // APP 2/28: sku desplegado en el celular
  const abrirNota = (r, pos) => setGloboN({ r, pos });
  const cerrarNota = useCallback(() => setGloboN(null), []);
  useEffect(() => { // los popovers fijos se cierran al hacer scroll
    if (!globo && !globoT) return undefined;
    const cerrar = () => { setGlobo(null); setGloboT(null); };
    window.addEventListener('scroll', cerrar, true);
    return () => window.removeEventListener('scroll', cerrar, true);
  }, [globo, globoT]);
  // Bloque 5: contador "Pendientes de biblia (N)" = temporales sin ligar.
  const contarPendientes = useCallback(async () => {
    if (!esSuperAdmin || esAgente) return;
    const { count, error: e } = await supabase.from('top_tours_temporales').select('id', { count: 'exact', head: true }).is('sku_biblia', null);
    setNPendientes(e ? null : count);
  }, [esSuperAdmin, esAgente]);
  useEffect(() => { contarPendientes(); }, [contarPendientes]);

  const showToast = (tipo, msg) => { setToast({ tipo, msg }); setTimeout(() => setToast(null), 3500); };
  const quien = loggedAgent?.nombre || 'admin';

  // Guarda cambios (nunca delete). `filas` = objeto o arreglo de { sku, ...cambios }.
  //  · Producto de biblia → upsert por sku en top_tours_precios (máscara).
  //  · Temporal (Bloque 5, es_temporal) → update por sku (TMP-xxxx, único) en top_tours_temporales.
  //    Los nombres de columna (activo, estrella, orden_top, precio_publico_*, notas) son los mismos.
  const guardar = async (filas, okMsg) => {
    setBusy(true);
    const lista = (Array.isArray(filas) ? filas : [filas]).map((f) => ({ ...f, actualizado_por: quien }));
    // Cambio instantáneo en pantalla (⭐, prendido, orden, precio). Si el guardado falla, la recarga lo
    // revierte; si sale bien, se vuelve a aplicar encima de la recarga por si ésta llega con datos viejos.
    const porSku = new Map(lista.map((x) => [x.sku, x]));
    const aplicar = () => setRows((prev) => prev.map((r) => {
      const c = porSku.get(r.sku);
      if (!c) return r;
      const n = { ...r };
      if ('estrella' in c) { n.estrella = c.estrella; n.seccion = c.estrella ? 1 : grupoBase(r); }
      if ('orden_top' in c) n.orden_top = c.orden_top;
      if ('activo' in c) n.activo_tt = c.activo;
      if ('precio_publico_adulto' in c) n.precio_adulto = c.precio_publico_adulto ?? r.sugerido_adulto;
      if ('precio_publico_menor' in c) n.precio_menor = c.precio_publico_menor ?? r.sugerido_menor;
      if ('precio_publico_adulto' in c || 'precio_publico_menor' in c) {
        n.precio_editado = !(Math.abs((Number(n.precio_adulto) || 0) - (Number(r.sugerido_adulto) || 0)) < 0.005
          && (r.sugerido_menor == null || Math.abs((Number(n.precio_menor) || 0) - (Number(r.sugerido_menor) || 0)) < 0.005));
      }
      return n;
    }));
    aplicar();
    // Bloque 7: los temporales se editan por su id (temporal_id de la vista), no por sku.
    const idTmp = new Map(rows.filter((r) => r.es_temporal && r.temporal_id != null).map((r) => [r.sku, r.temporal_id]));
    const deBiblia = lista.filter((f) => !idTmp.has(f.sku));
    const temporales = lista.filter((f) => idTmp.has(f.sku));
    let e = null;
    if (deBiblia.length) {
      ({ error: e } = await supabase.from('top_tours_precios').upsert(deBiblia, { onConflict: 'sku' }));
    }
    if (!e && temporales.length) {
      const res = await Promise.all(temporales.map(({ sku, ...cambios }) => supabase.from('top_tours_temporales').update(cambios).eq('id', idTmp.get(sku))));
      e = res.find((x) => x.error)?.error || null;
    }
    setBusy(false);
    if (e) { showToast('error', `No se pudo guardar: ${e.message}`); await load(); return false; } // la recarga revierte el cambio en pantalla
    showToast('ok', okMsg || 'Guardado');
    await load();
    aplicar();
    return true;
  };

  const maxOrden = () => rows.reduce((m, r) => (r.estrella && r.orden_top != null ? Math.max(m, Number(r.orden_top)) : m), 0);

  const toggleEstrella = (r) => (r.estrella
    ? guardar({ sku: r.sku, estrella: false, orden_top: null }, `⭐ quitada: ${r.nombre}`)
    : guardar({ sku: r.sku, estrella: true, orden_top: maxOrden() + 1 }, `⭐ agregada: ${r.nombre}`));

  const toggleActivo = (r) => guardar({ sku: r.sku, activo: !r.activo_tt }, `${r.activo_tt ? 'Apagado' : 'Prendido'}: ${r.nombre}`);

  const mover = (lista, i, dir) => {
    const a = lista[i], b = lista[i + dir];
    if (!a || !b) return;
    // Si hay órdenes repetidos o vacíos, se renumera la sección Top completa.
    const ordenes = lista.map((r) => r.orden_top);
    const limpios = ordenes.every((o) => o != null) && new Set(ordenes).size === ordenes.length;
    if (limpios) {
      guardar([{ sku: a.sku, orden_top: b.orden_top }, { sku: b.sku, orden_top: a.orden_top }], 'Orden actualizado');
    } else {
      const nueva = [...lista];
      [nueva[i], nueva[i + dir]] = [nueva[i + dir], nueva[i]];
      guardar(nueva.map((r, k) => ({ sku: r.sku, orden_top: k + 1 })), 'Orden actualizado');
    }
  };

  const proveedorMasivo = (codigo, nombre, activo) => {
    // Por proveedor (clave visible), para que los temporales con proveedor "Otro" no se mezclen entre sí.
    const skus = rows.filter((r) => provKey(r) === nombre).map((r) => r.sku);
    if (!skus.length) return;
    const verbo = activo ? 'PRENDER' : 'APAGAR';
    if (!window.confirm(`¿${verbo} los ${skus.length} productos de ${nombre} en Top Tours?\n(Incluye sus promociones y los que tengan ⭐.)`)) return;
    guardar(skus.map((sku) => ({ sku, activo })), `${nombre}: ${skus.length} producto(s) ${activo ? 'prendidos' : 'apagados'}`);
  };

  // ── Edición de precios ──
  const draftDe = (r) => drafts[r.sku] || { ad: r.precio_adulto ?? '', men: r.precio_menor ?? '' };
  const setDraft = (r, campo, v) => setDrafts((d) => ({ ...d, [r.sku]: { ...draftDe(r), [campo]: v } }));
  const erroresDe = (r, d) => {
    const errs = [];
    const ad = numOrNull(d.ad), men = numOrNull(d.men);
    if (d.ad !== '' && ad == null) errs.push('Precio adulto no es un número');
    if (d.men !== '' && d.men != null && men == null) errs.push('Precio menor no es un número');
    if (ad != null && r.precio_neto_adulto != null && ad < Number(r.precio_neto_adulto)) errs.push(`Adulto menor al neto (${money(r.precio_neto_adulto, r.moneda)})`);
    if (men != null && r.precio_neto_menor != null && men < Number(r.precio_neto_menor)) errs.push(`Menor menor al neto (${money(r.precio_neto_menor, r.moneda)})`);
    return errs;
  };
  const cambiado = (r, d) => !mismo(numOrNull(d.ad), r.precio_adulto) || (r.sugerido_menor != null && !mismo(numOrNull(d.men), r.precio_menor));
  const guardarPrecio = async (r) => {
    const d = draftDe(r);
    if (erroresDe(r, d).length) return;
    // Vacío o igual al sugerido = sin edición (null → hereda el sugerido).
    const norm = (v, sug) => { const n = numOrNull(v); return n == null || mismo(n, sug) ? null : n; };
    const cambios = { sku: r.sku, precio_publico_adulto: norm(d.ad, r.sugerido_adulto) };
    if (r.sugerido_menor != null) cambios.precio_publico_menor = norm(d.men, r.sugerido_menor);
    if (await guardar(cambios, `Precio guardado: ${r.nombre}`)) setDrafts((x) => { const n = { ...x }; delete n[r.sku]; return n; });
  };
  const volverSugerido = async (r) => {
    if (await guardar({ sku: r.sku, precio_publico_adulto: null, precio_publico_menor: null }, `↺ Sugerido: ${r.nombre}`)) {
      setDrafts((x) => { const n = { ...x }; delete n[r.sku]; return n; });
    }
  };

  // ── 3b: acciones del globo ──
  const igualarSala = async (r) => {
    const cambios = { sku: r.sku, precio_publico_adulto: r.precio_biblia_adulto ?? null };
    if (r.sugerido_menor != null) cambios.precio_publico_menor = r.precio_biblia_menor ?? null;
    if (await guardar(cambios, `Igualado a Sala: ${r.nombre}`)) {
      setGlobo(null);
      setDrafts((x) => { const n = { ...x }; delete n[r.sku]; return n; });
    }
  };
  const editarPrecio = (r) => {
    setGlobo(null);
    const el = inputsAd.current[r.sku];
    if (el) { el.focus(); el.select(); }
  };

  // ── Filtrado ──
  const ql = q.trim().toLowerCase();
  const base = esAgente ? rows.filter((r) => r.activo_tt) : rows;
  const alertas = esAgente ? [] : rows.filter(enAlerta);
  const verSoloAlerta = !esAgente && soloAlerta;
  const visibles = base.filter((r) => coincide(r, ql) && (esAgente || mostrarApagados || r.activo_tt) && (!verSoloAlerta || enAlerta(r)));
  const { top, normales, promos } = agrupar(visibles);
  const total = base.length;
  const prendidos = base.filter((r) => r.activo_tt).length;
  const estrellas = base.filter((r) => r.estrella && (!esAgente || r.activo_tt)).length;

  // ── Tabla ──
  // Bloque 12 §1c: badge NAL (amarillo, como en Sala).
  const celdaNAL = (r) => (
    <td style={tdP}>
      <span style={{ background: r.es_nacional ? `${C.yellow}20` : `${C.muted}20`, color: r.es_nacional ? C.yellow : C.muted, padding: '2px 6px', borderRadius: RADIO_INSIGNIA, fontSize:12, fontWeight: 700 }}>{r.es_nacional ? 'NAL' : 'EXT'}</span>
    </td>
  );
  // Bloque 09: botón "📄 PDF" (descriptivo). Agente TT → solo Precio Top Tours; admin → según su rol (App).
  const opcionesPDF = esAgente ? ['tt'] : (opcionesPrecioPDF || ['tt']);
  const celdaPDF = (r) => (
    <td style={{ ...tdP, whiteSpace: 'nowrap' }}>
      <DescriptivoBoton C={C} loggedAgent={loggedAgent} esAgente={esAgente} opcionesPrecio={opcionesPDF}
        leyendasBloqueadas={esAgente || leyendasBloqueadasPDF}
        producto={{ sku: r.sku, nombre: r.nombre, moneda: r.moneda, proveedor_codigo: r.proveedor_codigo, es_nacional: r.es_nacional,
          es_temporal: !!r.es_temporal, incluye: r.incluye, notas: r.notas }} />
    </td>
  );

  const marcaTemporal = (
    <span title="Producto temporal: todavía no está en la biblia" style={{ marginLeft: 6, background: `${C.yellow}25`, color: C.yellow, border: `1px solid ${C.yellow}55`, padding: '1px 6px', borderRadius: RADIO_INSIGNIA, fontSize:12, fontWeight: 700, whiteSpace: 'nowrap' }}>{<Ic n="Hourglass" chico />} Temporal — falta en biblia</span>
  );
  const inputSt = (err) => ({ width: 78, // APP 2/28: cabe en la columna pareja junto al globo de alerta
    height: 32, boxSizing: 'border-box', background: C.dark, border: `1px solid ${err ? C.red : C.border}`, borderRadius: RADIO, padding: '0 8px', color: C.text, fontSize: 12, fontFamily: 'var(--au-font-text)', outline: 'none', boxSizing: 'border-box' });


  const filaAgente = (r) => (
    <tr key={r.sku} style={{ borderBottom: `1px solid ${C.border}`, height: ALTO_FILA, background: r.es_temporal ? `${C.yellow}14` : 'transparent', opacity: noDisp(r) ? 0.55 : 1, filter: noDisp(r) ? 'grayscale(1)' : 'none' }}>
      <td style={{ ...tdP, fontSize: 12, color: C.muted, fontWeight: 700, whiteSpace: 'nowrap' }}><span style={linea1}>{r.sku}</span></td>
      <td style={{ ...tdP, color: C.text, fontWeight: 500 }} title={r.nombre}><div style={clamp2}>{r.estrella && <span title="Top" style={{ marginRight: 4 }}>{<Ic n="Star" chico />}</span>}{r.nombre}{r.es_temporal && marcaTemporal}<ChipNoDisponible C={C} r={r} /></div></td>
      <td style={tdP}><MonedaBadge C={C} moneda={r.moneda} /></td>
      <td style={numP(C, true)}>{money(r.precio_adulto, r.moneda)}</td>
      <td style={numP(C)}>{r.precio_menor == null ? '—' : money(r.precio_menor, r.moneda)}</td>
      <td style={numP(C, true)}>{money(r.precio_neto_adulto, r.moneda)}</td>
      <td style={numP(C)}>{money(r.precio_neto_menor, r.moneda)}</td>
      {celdaNAL(r)}
      <CeldaNota C={C} r={r} movil={movil} onAbrir={abrirNota} />
      {celdaPDF(r)}
    </tr>
  );


  const filaAdmin = (r, ctx) => {
    const d = draftDe(r);
    const errs = puedeEditar ? erroresDe(r, d) : [];
    const sucio = puedeEditar && cambiado(r, d);
    const menorDeshab = r.sugerido_menor == null;
    const alerta = enAlerta(r);
    const btn = (etq, titulo, onClick, primario, deshab) => <button type="button" className={`au-btn ${primario ? 'au-btn--primary' : 'au-btn--secondary'}`} disabled={busy || deshab} title={titulo} onClick={onClick} style={{ height: 26, boxSizing: 'border-box', padding: '0 7px', fontSize: 12, lineHeight: '24px' }}>{etq}</button>;
    return (
      <tr key={r.sku} style={{ borderBottom: `1px solid ${C.border}`, height: ALTO_FILA, opacity: r.activo_tt ? 1 : 0.5, background: alerta ? `${C.red}12` : r.es_temporal ? `${C.yellow}14` : 'transparent' }}>
        <td style={{ ...tdP, whiteSpace: 'nowrap' }}>
          {puedeEditar ? (
            <button type="button" disabled={busy} onClick={() => toggleEstrella(r)} title={r.estrella ? 'Quitar de Top' : 'Poner en Top'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, filter: r.estrella ? 'none' : 'grayscale(1)', opacity: r.estrella ? 1 : 0.35, padding: 0 }}>{<Ic n="Star" chico />}</button>
          ) : (r.estrella ? '⭐' : '')}
          {ctx && puedeEditar && (
            <span style={{ marginLeft: 4, display: 'inline-flex', gap: 1 }}>
              <button type="button" className="au-btn au-btn--ghost" disabled={busy || ctx.i === 0} onClick={() => mover(ctx.lista, ctx.i, -1)} style={{ padding: '2px 4px', fontSize: 12 }}>↑</button>
              <button type="button" className="au-btn au-btn--ghost" disabled={busy || ctx.i === ctx.lista.length - 1} onClick={() => mover(ctx.lista, ctx.i, 1)} style={{ padding: '2px 4px', fontSize: 12 }}>↓</button>
            </span>
          )}
        </td>
        <td style={tdP}>
          {puedeEditar ? (
            <button type="button" disabled={busy} onClick={() => toggleActivo(r)} className="au-switch" data-on={!!r.activo_tt} style={{ display: 'inline-block' }}>
              <span className="au-switch__knob" />
            </button>
          ) : (
            <span className={`au-badge ${r.activo_tt ? 'au-badge--success' : 'au-badge--neutral'}`} style={{ fontSize: 12 }}>{r.activo_tt ? 'Sí' : 'No'}</span>
          )}
        </td>
        <td style={{ ...tdP, fontSize: 12, color: C.muted, fontWeight: 700, whiteSpace: 'nowrap' }}><span style={linea1}>{r.sku}</span></td>
        <td style={{ ...tdP, color: C.text, fontWeight: 500, opacity: noDisp(r) ? 0.6 : 1 }} title={errs.length ? errs.join(' · ') : r.nombre}>
          <div style={clamp2}>
            {errs.length > 0 && <span title={errs.join(' · ')} style={{ color: C.red, fontWeight: 800, marginRight: 4 }}>!</span>}
            {r.nombre}
            {r.es_temporal && globoTemporal(r)}
            <ChipNoDisponible C={C} r={r} />
            {r.precio_editado && <span className="au-badge au-badge--yellow" style={{ fontSize: 12, marginLeft: 6 }}>editado</span>}
          </div>
        </td>
        <td style={tdP}><MonedaBadge C={C} moneda={r.moneda} /></td>
        <td style={{ ...tdP, whiteSpace: 'nowrap', textAlign: 'right' }}>
          {puedeEditar
            ? <input type="number" step="0.01" min="0" value={d.ad} ref={(el) => { inputsAd.current[r.sku] = el; }} onChange={(e) => setDraft(r, 'ad', e.target.value)} style={inputSt(errs.some((x) => x.startsWith('Adulto') || x.includes('adulto')))} />
            : <span style={{ fontWeight: 700, color: C.text, fontVariantNumeric: 'tabular-nums' }}>{money(r.precio_adulto, r.moneda)}</span>}
          {alerta && globoAlerta(r)}
        </td>
        <td style={{ ...tdP, whiteSpace: 'nowrap', textAlign: 'right' }}>
          {puedeEditar
            ? <input type="number" step="0.01" min="0" disabled={menorDeshab} value={menorDeshab ? '' : d.men} placeholder={menorDeshab ? 'n/a' : ''}
                onChange={(e) => setDraft(r, 'men', e.target.value)} style={{ ...inputSt(errs.some((x) => x.startsWith('Menor') || x.includes('menor no'))), opacity: menorDeshab ? 0.4 : 1 }} />
            : <span style={{ color: C.muted, fontVariantNumeric: 'tabular-nums' }}>{r.precio_menor == null ? '—' : money(r.precio_menor, r.moneda)}</span>}
        </td>
        <td style={numP(C, true)}>{money(r.precio_neto_adulto, r.moneda)}</td>
        <td style={numP(C)}>{money(r.precio_neto_menor, r.moneda)}</td>
        <td style={{ ...tdP, fontSize: 12, color: C.muted, whiteSpace: 'nowrap', textAlign: 'right' }} title="Precio sugerido (adulto / menor)">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end', maxWidth: '100%' }}>
            <span style={{ ...linea1, fontVariantNumeric: 'tabular-nums' }}>{money(r.sugerido_adulto, r.moneda)}{r.sugerido_menor != null && <> / {money(r.sugerido_menor, r.moneda)}</>}</span>
            {puedeEditar && sucio && btn('✓', 'Guardar precio', () => guardarPrecio(r), true, errs.length > 0)}
            {puedeEditar && r.precio_editado && btn('↺', 'Volver al sugerido', () => volverSugerido(r), false)}
          </span>
        </td>
        {celdaNAL(r)}
        <CeldaNota C={C} r={r} movil={movil} onAbrir={abrirNota} />
        {celdaPDF(r)}
      </tr>
    );
  };

  // 3b — globo rojo + popover (solo admins en el editor; los agentes no lo ven).
  const globoAlerta = (r) => {
    const abierto = globo?.sku === r.sku;
    // El popover va en position:fixed (las tarjetas recortan con overflow:hidden).
    const abrir = (e) => {
      if (abierto) { setGlobo(null); return; }
      const b = e.currentTarget.getBoundingClientRect();
      const ancho = 330;
      setGlobo({ sku: r.sku, top: Math.min(b.bottom + 6, window.innerHeight - 260), left: Math.max(8, Math.min(b.left - 10, window.innerWidth - ancho - 8)) });
    };
    const linea = (etq, tt, sala, neto) => {
      if (!arriba(tt, sala)) return null;
      const dif = Number(tt) - Number(sala);
      const pct = Number(sala) > 0 ? (dif / Number(sala)) * 100 : 0;
      const margen = neto != null ? Number(sala) - Number(neto) : null;
      const std = SOBRE_STD[r.moneda] ?? SOBRE_STD.MXN;
      return (
        <div key={etq} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>{etq}</div>
          <div>Precio Top Tours: {money(tt, r.moneda)} · Público Sala: {money(sala, r.moneda)} · <strong style={{ color: C.red }}>Está {money(dif, r.moneda)} arriba ({pct.toFixed(1)}%)</strong></div>
          {margen != null && (
            <div style={{ color: C.muted, marginTop: 3 }}>
              Margen real del proveedor: {money(sala, r.moneda)} − {money(neto, r.moneda)} = {money(margen, r.moneda)}
              {margen < std ? `, menor al sobreprecio estándar (${r.moneda === 'USD' ? 'USD 19.99' : '$299 MXN'}).` : '.'}
            </div>
          )}
        </div>
      );
    };
    return (
      <span style={{ position: 'relative', display: 'inline-block', marginLeft: 6, verticalAlign: 'middle' }}>
        <button type="button" onClick={abrir} title="Precio arriba de Sala de Ventas"
          style={{ background: 'transparent', color: C.red, border: `2px solid ${C.red}`, borderRadius: RADIO, width: 22, height: 22, fontSize: 12, fontWeight: 800, cursor: 'pointer', lineHeight: '18px', padding: 0, boxSizing: 'border-box' }}>!</button>
        {abierto && (
          <div style={{ position: 'fixed', zIndex: 300, top: globo.top, left: globo.left, width: 330, background: C.surface, border: `1px solid ${C.red}`, borderRadius: RADIO_TARJETA, padding: 14, boxShadow:'var(--au-shadow-card)', fontSize: 12, color: C.text, whiteSpace: 'normal', textAlign: 'left' }}>
            <div style={{ fontWeight: 700, color: C.red, marginBottom: 8 }}>{<Ic n="AlertTriangle" chico />} Precio arriba de Sala de Ventas · {r.sku}</div>
            {linea('Adulto', r.precio_adulto, r.precio_biblia_adulto, r.precio_neto_adulto)}
            {linea('Menor', r.precio_menor, r.precio_biblia_menor, r.precio_neto_menor)}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
              {puedeEditar && <button type="button" className="au-btn au-btn--primary" disabled={busy} onClick={() => igualarSala(r)} style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12 }}>Igualar al público de Sala</button>}
              {puedeEditar && <button type="button" className="au-btn au-btn--secondary" onClick={() => editarPrecio(r)} style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12 }}>Editar precio</button>}
              <button type="button" className="au-btn au-btn--ghost" onClick={() => setGlobo(null)} style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12 }}>Dejar así</button>
            </div>
          </div>
        )}
      </span>
    );
  };

  // Bloque 5 — globo ámbar "⏳ Temporal — falta en biblia" (editor: creado por, fecha y SKU TMP).
  const globoTemporal = (r) => {
    const abierto = globoT?.sku === r.sku;
    const abrir = (e) => {
      if (abierto) { setGloboT(null); return; }
      const b = e.currentTarget.getBoundingClientRect();
      setGloboT({ sku: r.sku, top: Math.min(b.bottom + 6, window.innerHeight - 200), left: Math.max(8, Math.min(b.left - 10, window.innerWidth - 300 - 8)) });
    };
    const fecha = r.temporal_creado_en ? new Date(r.temporal_creado_en).toLocaleString('es-MX', { timeZone: 'America/Cancun', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
    return (
      <span style={{ display: 'inline-block', marginLeft: 6, verticalAlign: 'middle' }}>
        <button type="button" onClick={abrir} title="Producto temporal: todavía no está en la biblia"
          style={{ background: `${C.yellow}25`, color: C.yellow, border: `1px solid ${C.yellow}55`, padding: '1px 7px', borderRadius: RADIO_INSIGNIA, fontSize:12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>{<Ic n="Hourglass" chico />} Temporal — falta en biblia</button>
        {abierto && (
          <div style={{ position: 'fixed', zIndex: 300, top: globoT.top, left: globoT.left, width: 300, background: C.surface, border: `1px solid ${C.yellow}`, borderRadius: RADIO_TARJETA, padding: 14, boxShadow:'var(--au-shadow-card)', fontSize: 12, color: C.text, whiteSpace: 'normal', textAlign: 'left', fontWeight: 400 }}>
            <div style={{ fontWeight: 700, color: C.yellow, marginBottom: 8 }}>{<Ic n="Hourglass" chico />} Temporal — falta en biblia</div>
            <div><strong>SKU:</strong> {r.sku}</div>
            <div><strong>Creado por:</strong> {r.temporal_creado_por || '—'}</div>
            <div><strong>Fecha:</strong> {fecha}</div>
            <div style={{ color: C.muted, fontSize: 12, marginTop: 6 }}>Marco lo liga a la biblia desde "Pendientes de biblia".</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              {puedeEditar && r.temporal_id != null && <button type="button" className="au-btn au-btn--secondary" onClick={() => { setGloboT(null); setFormTmp({ modo: 'editar', sku: r.sku, temporalId: r.temporal_id }); }} style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12 }}>Editar datos</button>}
              <button type="button" className="au-btn au-btn--ghost" onClick={() => setGloboT(null)} style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12 }}>Cerrar</button>
            </div>
          </div>
        )}
      </span>
    );
  };


  // APP 2/28 (RP-10): tabla pareja (mismas columnas, orden y ancho; renglones del mismo alto) y renglón compacto en el celular.
  const listaMovil = (items) => (
    <div>
      {items.map((r) => {
        const d = draftDe(r); const errs = puedeEditar ? erroresDe(r, d) : []; const sucio = puedeEditar && cambiado(r, d);
        return <FilaMovil key={r.sku} C={C} r={r} money={money} pubAd={r.precio_adulto} netoAd={r.precio_neto_adulto} abierto={abiertoM === r.sku} onToggle={() => setAbiertoM((v) => (v === r.sku ? null : r.sku))} onAbrirNota={abrirNota}
          apagado={!esAgente && !r.activo_tt} cabecera={r.estrella ? <span title="Top" style={{ marginRight: 4 }}>{<Ic n="Star" chico />}</span> : null}
          detalle={<>
            <DatoMovil C={C} l="Pub ME" v={r.precio_menor == null ? '—' : money(r.precio_menor, r.moneda)} />
            <DatoMovil C={C} l="Neto ME" v={money(r.precio_neto_menor, r.moneda)} />
            <MonedaBadge C={C} moneda={r.moneda} />
            <span style={{ background: r.es_nacional ? `${C.yellow}20` : `${C.muted}20`, color: r.es_nacional ? C.yellow : C.muted, padding: '2px 6px', borderRadius: RADIO_INSIGNIA, fontSize: 12, fontWeight: 700 }}>{r.es_nacional ? 'NAL' : 'EXT'}</span>
            <span style={{ fontSize: 12, color: C.muted }}>{r.sku}</span>
            {r.es_temporal && (esAgente ? marcaTemporal : globoTemporal(r))}<ChipNoDisponible C={C} r={r} />
            <table style={{ borderCollapse: 'collapse' }}><tbody><tr>{celdaPDF(r)}</tr></tbody></table>
            {!esAgente && (
              <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
                {puedeEditar && <button type="button" disabled={busy} onClick={() => toggleActivo(r)} className="au-switch" data-on={!!r.activo_tt} style={{ display: 'inline-block' }}><span className="au-switch__knob" /></button>}
                {puedeEditar && <button type="button" disabled={busy} onClick={() => toggleEstrella(r)} title={r.estrella ? 'Quitar de Top' : 'Poner en Top'} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, filter: r.estrella ? 'none' : 'grayscale(1)', opacity: r.estrella ? 1 : 0.35, padding: 0 }}>{<Ic n="Star" chico />}</button>}
                {puedeEditar && <input type="number" step="0.01" min="0" value={d.ad} onChange={(e) => setDraft(r, 'ad', e.target.value)} aria-label="Precio adulto" style={inputSt(errs.length > 0)} />}
                {puedeEditar && <input type="number" step="0.01" min="0" disabled={r.sugerido_menor == null} value={r.sugerido_menor == null ? '' : d.men} onChange={(e) => setDraft(r, 'men', e.target.value)} aria-label="Precio menor" style={{ ...inputSt(false), opacity: r.sugerido_menor == null ? 0.4 : 1 }} />}
                {puedeEditar && sucio && <button type="button" className="au-btn au-btn--primary" disabled={busy || errs.length > 0} onClick={() => guardarPrecio(r)} style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12 }}>Guardar</button>}
                {puedeEditar && r.precio_editado && <button type="button" className="au-btn au-btn--secondary" disabled={busy} onClick={() => volverSugerido(r)} style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12 }}>↺</button>}
                {errs.length > 0 && <span style={{ color: C.red, fontSize: 12, fontWeight: 600, width: '100%' }}>{errs.join(' · ')}</span>}
              </span>
            )}
          </>} />;
      })}
    </div>
  );
  const tabla = (items, conFlechas) => (movil ? listaMovil(items) : (
    <TablaPareja C={C} cols={esAgente ? COLS_AGENTE : COLS_ADMIN}>
      {items.map((r, i) => (esAgente ? filaAgente(r) : filaAdmin(r, conFlechas ? { lista: items, i } : null)))}
    </TablaPareja>
  ));

  const grupoProveedor = (g, prefijo) => {
    const enGrupo = esAgente ? g.items.length : rows.filter((r) => provKey(r) === g.proveedor && grupoBase(r) === grupoBase(g.items[0])).length;
    const prendGrupo = rows.filter((r) => provKey(r) === g.proveedor && grupoBase(r) === grupoBase(g.items[0]) && r.activo_tt).length;
    return (
      <Grupo key={`${prefijo}-${g.proveedor}`} C={C} titulo={g.proveedor}
        derecha={<>
          <span style={{ fontSize: 12, color: C.muted }}>{esAgente ? `${enGrupo} producto${enGrupo !== 1 ? 's' : ''}` : `${prendGrupo}/${enGrupo} prendidos`}</span>
          {puedeEditar && <>
            <button type="button" className="au-btn au-btn--ghost" disabled={busy} onClick={() => proveedorMasivo(g.codigo, g.proveedor, false)} style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12 }}>Apagar proveedor</button>
            <button type="button" className="au-btn au-btn--secondary" disabled={busy} onClick={() => proveedorMasivo(g.codigo, g.proveedor, true)} style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12 }}>Prender proveedor</button>
          </>}
        </>}>
        {tabla(g.items, false)}
      </Grupo>
    );
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>Cargando lista de Top Tours…</div>;

  return (
    <>
      {toast && (
        <div className={`au-banner ${toast.tipo === 'error' ? 'au-banner--error' : 'au-banner--success'}`} style={{ position: 'fixed', top: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 400, fontWeight: 600, boxShadow:'var(--au-shadow-card)' }}>{toast.msg}</div>
      )}
      <Titulo right={!esAgente && !puedeEditar ? <span className="au-badge au-badge--neutral" style={{ fontSize: 12 }}>Solo lectura</span>
        : puedeEditar ? <button type="button" className="au-btn au-btn--primary" onClick={() => setFormTmp({ modo: 'alta' })} style={{ height: 32, boxSizing: 'border-box', padding: '0 14px', fontSize: 12 }}>{<Ic n="PlusCircle" chico />} Agregar producto temporal</button> : null}>
      {globoN && <GloboNota C={C} r={globoN.r} pos={globoN.pos} movil={movil} onCerrar={cerrarNota} />}{/* APP 2/28 */}
        Lista de Precios · Top Tours
      </Titulo>
      {error && <div className="au-banner au-banner--error" style={{ marginBottom: 12 }}>{error}</div>}
      {formTmp && (
        <TemporalForm C={C} modo={formTmp.modo} sku={formTmp.sku} temporalId={formTmp.temporalId} quien={quien}
          onClose={() => setFormTmp(null)}
          onGuardado={async (msg) => { showToast('ok', msg); await load(); contarPendientes(); }} />
      )}
      {/* Bloque 5: subpestaña "Pendientes de biblia" — solo Marco (super_admin) */}
      {!esAgente && esSuperAdmin && (
        <div style={{ marginBottom: 12 }}>
          <div className="au-toggle">
            <button type="button" aria-pressed={vista === 'lista'} onClick={() => setVista('lista')}>Lista Top Tours</button>
            <button type="button" aria-pressed={vista === 'pendientes'} onClick={() => setVista('pendientes')}>{<Ic n="ClipboardList" chico />} Pendientes de biblia ({nPendientes ?? '…'})</button>
          </div>
        </div>
      )}
      {!esAgente && esSuperAdmin && vista === 'pendientes' ? (
        <PendientesBiblia C={C} quien={quien} onLigado={async (msg) => { showToast('ok', msg); await load(); contarPendientes(); }} />
      ) : (<>

      {/* Bloque 12 §1c: regla de precio visible para el agente Top Tours */}
      {esAgente && (
        <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: RADIO_TARJETA, background: `${C.blue}12`, border: `1px solid ${C.blue}35`, fontSize: 12, color: C.text }}>
          Tu precio de venta = <strong>neto + $299 MXN</strong> (productos en pesos) o <strong>+ US$19.99</strong> (productos en dólares).
        </div>
      )}

      <Buscador C={C} value={q} onChange={setQ}
        derecha={esAgente
          ? `${prendidos} producto${prendidos !== 1 ? 's' : ''} · ${estrellas} ⭐`
          : `${total} total · ${prendidos} prendidos · ${estrellas} ⭐`}>
        <button type="button" onClick={() => descargarListaTT(visibles.filter((r) => r.activo_tt))}
          style={{ background: `${C.grad}`, color: '#fff', border: 'none', borderRadius: RADIO, height: 40, boxSizing: 'border-box', padding: '0 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--au-font-text)', whiteSpace: 'nowrap' }}>{<Ic n="FileText" chico />} Descargar PDF</button>
        {!esAgente && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.muted, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <button type="button" onClick={() => setMostrarApagados((v) => !v)} className="au-switch" data-on={mostrarApagados} style={{ display: 'inline-block' }}>
              <span className="au-switch__knob" />
            </button>
            Mostrar apagados
          </label>
        )}
      </Buscador>

      {!esAgente && alertas.length > 0 && (
        <div className="au-banner au-banner--error" style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600 }}>{<Ic n="AlertTriangle" chico />} {alertas.length} producto{alertas.length !== 1 ? 's' : ''} arriba del precio de Sala</span>
          <button type="button" className="au-btn au-btn--secondary" onClick={() => setSoloAlerta((v) => !v)} style={{ height: 32, boxSizing: 'border-box', padding: '0 12px', fontSize: 12 }}>
            {soloAlerta ? 'Ver todos' : 'Ver solo estos'}
          </button>
        </div>
      )}

      {visibles.length === 0 ? (
        <div className="au-glass" style={{ textAlign: 'center', padding: 40, color: C.muted }}>
          <div style={{ fontSize: 30, marginBottom: 8 }}>{<Ic n="Search" chico />}</div>
          <div>{q ? `Sin resultados para "${q}"` : 'Sin productos'}</div>
        </div>
      ) : (
        <>
          {top.length > 0 && (
            <Grupo C={C} acento={C.yellow} titulo="Top" derecha={<span style={{ fontSize: 12, color: C.muted }}>{top.length} producto{top.length !== 1 ? 's' : ''}</span>}>
              {tabla(top, !ql)}
            </Grupo>
          )}
          {normales.map((g) => grupoProveedor(g, 'n'))}
          {promos.length > 0 && (
            <>
              <Titulo>Promociones</Titulo>
              {promos.map((g) => grupoProveedor(g, 'p'))}
            </>
          )}
        </>
      )}
      </>)}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// COMPARATIVO — Sala (biblia) vs Top Tours, solo lectura
// ════════════════════════════════════════════════════════════════════════════
export function ComparativoPreciosTab({ C: Cbase }) {
  const C = Cbase; // APP 2/20 Fase A: C.red viene del tema
  const { rows, loading, error } = useLista();
  const [q, setQ] = useState('');
  const ql = q.trim().toLowerCase();
  const filas = rows.filter((r) => coincide(r, ql)).sort((a, b) =>
    String(a.proveedor_nombre || '').localeCompare(String(b.proveedor_nombre || ''), 'es') || String(a.nombre).localeCompare(String(b.nombre), 'es'));
  const mayor = (tt, sala) => tt != null && sala != null && Number(tt) > Number(sala) + 0.005;
  const rojo = { background: `${C.red}18`, color: C.red, fontWeight: 700 };
  const nRojos = rows.filter((r) => mayor(r.precio_adulto, r.precio_biblia_adulto) || mayor(r.precio_menor, r.precio_biblia_menor)).length;

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>Cargando comparativo…</div>;

  return (
    <>
      <Titulo>Comparativo · Sala de Ventas vs Top Tours</Titulo>
      {error && <div className="au-banner au-banner--error" style={{ marginBottom: 12 }}>{error}</div>}
      <Buscador C={C} value={q} onChange={setQ} derecha={`${filas.length} producto${filas.length !== 1 ? 's' : ''} · ${nRojos} con precio TT > Sala`} />
      <div className="au-glass" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {['SKU', 'Producto', 'Proveedor', 'Mon', 'Neto Ad', 'Público Sala Ad', 'Precio TT Ad', 'Neto Men', 'Público Sala Men', 'Precio TT Men', 'Prendido TT'].map((h) => <th key={h} style={th(C)}>{h}</th>)}
            </tr></thead>
            <tbody>
              {filas.length === 0 ? (
                <tr><td colSpan={11} style={{ padding: 40, textAlign: 'center', color: C.muted }}>{q ? `Sin resultados para "${q}"` : 'Sin productos'}</td></tr>
              ) : filas.map((r) => (
                <tr key={r.sku} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ ...td, fontFamily: 'var(--au-font-text)', fontSize: 12, color: C.muted, fontWeight: 700, whiteSpace: 'nowrap' }}>{r.sku}</td>
                  <td style={{ ...td, color: C.text, fontWeight: 500 }}>{r.estrella && '⭐ '}{r.nombre}{r.es_promocion && <span className="au-badge au-badge--purple" style={{ fontSize: 12, marginLeft: 6 }}>promo</span>}{r.es_temporal && <span className="au-badge au-badge--yellow" style={{ fontSize: 12, marginLeft: 6 }}>{<Ic n="Hourglass" chico />} temporal</span>}</td>
                  <td style={{ ...td, fontSize: 12, color: C.muted }}>{r.proveedor_nombre || r.proveedor_codigo}</td>
                  <td style={td}><MonedaBadge C={C} moneda={r.moneda} /></td>
                  <td style={numTd(C)}>{money(r.precio_neto_adulto, r.moneda)}</td>
                  <td style={numTd(C, true)}>{money(r.precio_biblia_adulto, r.moneda)}</td>
                  <td style={{ ...numTd(C, true), ...(mayor(r.precio_adulto, r.precio_biblia_adulto) ? rojo : {}) }}>{money(r.precio_adulto, r.moneda)}</td>
                  <td style={numTd(C)}>{money(r.precio_neto_menor, r.moneda)}</td>
                  <td style={numTd(C, true)}>{money(r.precio_biblia_menor, r.moneda)}</td>
                  <td style={{ ...numTd(C, true), ...(mayor(r.precio_menor, r.precio_biblia_menor) ? rojo : {}) }}>{money(r.precio_menor, r.moneda)}</td>
                  <td style={td}><span className={`au-badge ${r.activo_tt ? 'au-badge--success' : 'au-badge--neutral'}`} style={{ fontSize: 12 }}>{r.activo_tt ? 'Sí' : 'No'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
